import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import i18n, { applyAccountLanguage } from '../i18n';

interface User {
  /** Account default from the regional office; the UI pick overrides it. */
  language?: string;
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  totp_enabled: boolean;
  agency_id: number | null;
  regional_office_id: number | null;
  currency: string;
  currency_rate: number;
  is_demo?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe: boolean, totpCode?: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  handleUnauthorized: () => void;
  updateUser: (updates: Partial<User>) => void;
}

interface LoginResult {
  success: boolean;
  requires_2fa?: boolean;
  error?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || '';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem('access_token')
  );

  // Mirror the demo (read-only) flag to localStorage so non-React code — the axios
  // request interceptor in services/api.ts — can block writes synchronously without
  // access to this context. Cleared on logout (user === null).
  useEffect(() => {
    if (user?.is_demo) localStorage.setItem('sense_is_demo', '1');
    else localStorage.removeItem('sense_is_demo');
  }, [user]);

  // Helper to make authenticated requests
  const authFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return fetch(`${API_BASE}${url}`, {
      ...options,
      headers,
      credentials: 'include', // for refresh token cookie
    });
  }, [accessToken]);

  // Refresh token
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.access_token);
        localStorage.setItem('access_token', data.access_token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authFetch('/auth/me');

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        applyAccountLanguage(data.language);
      } else if (response.status === 401) {
        // Check if account is blocked/paused
        const data = await response.json().catch(() => ({}));
        if (data.detail?.includes('paused') || data.detail?.includes('blocked')) {
          // Account suspended - clear everything and redirect
          setUser(null);
          setAccessToken(null);
          localStorage.removeItem('access_token');
          setIsLoading(false);
          return;
        }

        // Try to refresh token
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry fetch user
          const retryResponse = await authFetch('/auth/me');
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setUser(data);
            applyAccountLanguage(data.language);
          } else {
            setUser(null);
            setAccessToken(null);
            localStorage.removeItem('access_token');
          }
        } else {
          setUser(null);
          setAccessToken(null);
          localStorage.removeItem('access_token');
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, authFetch, refreshToken]);

  // Initial load
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Login
  const login = async (
    email: string,
    password: string,
    rememberMe: boolean,
    totpCode?: string
  ): Promise<LoginResult> => {
    try {
      const body: Record<string, unknown> = { email, password, remember_me: rememberMe };
      if (totpCode) {
        body.totp_code = totpCode;
      }

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if 2FA is required (backend returns 200 with requires_2fa flag)
        if (data.requires_2fa) {
          return { success: false, requires_2fa: true };
        }

        // Successful login with tokens
        setAccessToken(data.access_token);
        localStorage.setItem('access_token', data.access_token);
        setUser(data.user);
        return { success: true };
      }

      // Non-OK response: extract error message from detail
      return { success: false, error: data.detail || i18n.t('login.loginFailed') };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: i18n.t('common.networkError') };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear advertiser selection for this user
      const userId = user?.id;
      if (userId) localStorage.removeItem(`sense_selected_advertiser_${userId}`);
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem('access_token');
    }
  };

  // Update user data locally (e.g. after profile edit)
  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  // Handle unauthorized (401) - force logout and redirect
  const handleUnauthorized = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshToken,
        handleUnauthorized,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
